import React, { useState, useEffect } from 'react';
import Styles from './BlogsContainer.module.scss';
import { useNavigate } from 'react-router-dom';
import { getAllPostsData } from '../../service/communityService.js';
import { ThreeCircles } from 'react-loader-spinner';
import { urls } from '../../utils/urls.js';

const BlogsContainer = () => {
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleBlogs, setVisibleBlogs] = useState(6);
  const navigate = useNavigate();
  const publicUrl = process.env.PUBLIC_URL || '';

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await getAllPostsData();
        const data = await JSON.parse(response);
        setBlogs(data.blog);

        const categoriesSet = new Set();
        const subCategoriesSet = new Set();

        data.blog.forEach((blog) => {
          categoriesSet.add(blog.PostCategory);
          subCategoriesSet.add(blog.PostSubCategory);
        });

        setCategories([...categoriesSet]);
        setSubCategories([...subCategoriesSet]);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching blogs:', error);
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const handleCategoryChange = (e) => {
    setCategory(e.target.value.toLowerCase());
    setVisibleBlogs(6);
  };

  const handleSubCategoryChange = (e) => {
    setSubCategory(e.target.value.toLowerCase());
    setVisibleBlogs(6);
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setVisibleBlogs(6);
  };

  const filteredBlogs = blogs.filter((blog) => {
    const q = searchTerm.toLowerCase();
    const catOk =
      category === '' || (blog.PostCategory && blog.PostCategory.toLowerCase() === category);
    const subOk =
      subCategory === '' ||
      (blog.PostSubCategory && blog.PostSubCategory.toLowerCase() === subCategory);
    return blog.PostTitle.toLowerCase().includes(q) && catOk && subOk;
  });

  const loadMoreBlogs = () => {
    setVisibleBlogs((prev) => prev + 6);
  };

  const capitalizeWords = (str) => {
    return str
      .split(/[\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={Styles.pageWrapper}>
      <section className={Styles.hero} aria-label="Blogs hero">
        <div
          className={Styles.heroBgImage}
          style={{ backgroundImage: `url(${publicUrl}/background.png)` }}
          aria-hidden
        />
        <div className={Styles.heroOverlay} />
        <div className={Styles.heroInner}>
          <p className={Styles.heroEyebrow}>Resources</p>
          <p className={Styles.heroKicker}>Iron Lady</p>
          <h1 className={Styles.heroTitle}>Blogs</h1>
          <p className={Styles.heroLead}>
            Ideas, stories, and lessons from the community — for women who are building what comes next.
          </p>
        </div>
      </section>

      <section className={Styles.catalog} aria-label="Blog library">
        <div className={Styles.catalogInner}>
          {loading ? (
            <div className={Styles.loadingWrap}>
              <ThreeCircles
                height="70"
                width="70"
                color="#F52929"
                visible
                ariaLabel="Loading blogs"
              />
            </div>
          ) : (
            <>
              <div className={Styles.filters}>
                <div className={Styles.filterField}>
                  <label className={Styles.filterLabel} htmlFor="blogs-search">
                    Search
                  </label>
                  <input
                    id="blogs-search"
                    type="search"
                    className={Styles.filterInput}
                    placeholder="Search by title…"
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                    autoComplete="off"
                  />
                </div>
                <div className={Styles.filterField}>
                  <label className={Styles.filterLabel} htmlFor="blogs-category">
                    Category
                  </label>
                  <select
                    id="blogs-category"
                    className={Styles.filterSelect}
                    value={category}
                    onChange={handleCategoryChange}
                  >
                    <option value="">All categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {capitalizeWords(cat)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={Styles.filterField}>
                  <label className={Styles.filterLabel} htmlFor="blogs-subcategory">
                    Subcategory
                  </label>
                  <select
                    id="blogs-subcategory"
                    className={Styles.filterSelect}
                    value={subCategory}
                    onChange={handleSubCategoryChange}
                  >
                    <option value="">All subcategories</option>
                    {subCategories.map((subCat) => (
                      <option key={subCat} value={subCat}>
                        {capitalizeWords(subCat)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={Styles.grid} role="list">
                {filteredBlogs.length > 0 ? (
                  filteredBlogs.slice(0, visibleBlogs).map((blog) => (
                    <article
                      key={blog.id}
                      role="listitem"
                      tabIndex={0}
                      className={Styles.card}
                      onClick={() =>
                        navigate(
                          `/blogs/${blog.PostCategory}/${blog.PostSubCategory}/${blog.PostSlug}`,
                          {
                            state: {
                              id: blog.id,
                              slug: blog.PostSlug,
                              imageURL: blog.PostImageUrl,
                            },
                          }
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(
                            `/blogs/${blog.PostCategory}/${blog.PostSubCategory}/${blog.PostSlug}`,
                            {
                              state: {
                                id: blog.id,
                                slug: blog.PostSlug,
                                imageURL: blog.PostImageUrl,
                              },
                            }
                          );
                        }
                      }}
                    >
                      <div className={Styles.cardThumb}>
                        <img src={blog.PostImageUrl} alt="" />
                      </div>
                      <div className={Styles.cardBody}>
                        <span className={Styles.cardMeta}>
                          {capitalizeWords(blog.PostCategory)}
                          {blog.PostSubCategory ? ` · ${capitalizeWords(blog.PostSubCategory)}` : ''}
                        </span>
                        <h2 className={Styles.cardTitle}>{blog.PostTitle}</h2>
                        <time
                          className={Styles.cardDate}
                          dateTime={blog.PostDate ? new Date(blog.PostDate).toISOString() : undefined}
                        >
                          {new Date(blog.PostDate).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </time>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className={Styles.empty}>
                    <img src={urls.noData} alt="" />
                    <p className={Styles.emptyText}>No posts match your filters.</p>
                  </div>
                )}
              </div>

              {filteredBlogs.length > 0 && visibleBlogs < filteredBlogs.length && (
                <div className={Styles.loadMoreWrap}>
                  <button type="button" className={Styles.loadMore} onClick={loadMoreBlogs}>
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogsContainer;
